import React, {  useState, useEffect, useContext , useMemo } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Grid, ListItemText, MenuItem, Select, Avatar } from "@material-ui/core";
import { toast } from "react-toastify";
import ShowTicketOpen from "../ShowTicketOpenModal";
import IconChannel from "../IconChannel";
import { resolveContactAvatar, handleImageError } from "../../utils/avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

const useStyles = makeStyles((theme) => ({
  online: {
    fontSize: 11,
    color: "#25d366"
  },
  offline: {
    fontSize: 11,
    color: "#e1306c"
  },
  quickOption: {
    backgroundColor: "#e8f5e9",
    color: "#1b5e20",
    fontWeight: 600,
    borderRadius: 8,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    "&:hover": {
      backgroundColor: "#c8e6c9"
    }
  }
}));

const filter = createFilterOptions({
  trim: true,
});

function formatPhoneDisplay(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 13 && digits.startsWith("55")) {
    const d = digits.slice(2);
    return `+55 (${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }
  if (digits.length === 12 && digits.startsWith("55")) {
    const d = digits.slice(2);
    return `+55 (${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
  return raw;
}

function normalizeNumberForStorage(raw) {
  const digits = raw.replace(/\D/g, "");
  if ((digits.length === 10 || digits.length === 11) && !digits.startsWith("55")) {
    return `55${digits}`;
  }
  return digits;
}

const NewTicketModal = ({ modalOpen, onClose, initialContact, initialWhatsappId, initialChannel }) => {
  const classes = useStyles();
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [channelFilter, setChannelFilter] = useState(null);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newContact, setNewContact] = useState({});
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;

  const userAllowedWhatsapps = useMemo(() => {
    if (user?.profile === "admin") return whatsapps;
    const allowedIds = (user?.whatsapps || []).map((w) => w.id);
    if (user?.whatsappId) allowedIds.push(user.whatsappId);
    if (allowedIds.length === 0) return whatsapps;
    return whatsapps.filter((w) => allowedIds.includes(w.id));
  }, [whatsapps, user]);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
    if (initialChannel) {
      setChannelFilter(initialChannel);
    }
    if (initialWhatsappId) {
      setSelectedWhatsapp(initialWhatsappId);
    }
  }, [initialContact, initialWhatsappId, initialChannel]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => setWhatsapps(data));
      };

      if (user.whatsapps?.length === 1) {
        setSelectedWhatsapp(user.whatsapps[0].id);
      } else if (whatsappId !== null && whatsappId !== undefined) {
        setSelectedWhatsapp(whatsappId);
      }

      if (user.queues?.length === 1) {
        setSelectedQueue(user.queues[0].id);
      }
      fetchContacts();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [selectedContact, channelFilter]);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicketWithQueue = async (contactId, overrideQueueId) => {
    if (!contactId) return;
    const qId = overrideQueueId || selectedQueue;
    if (!qId) {
      toast.error("Selecione uma fila");
      return;
    }

    setLoading(true);
    try {
      const wId = selectedWhatsapp !== "" ? selectedWhatsapp : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        queueId: qId,
        whatsappId: wId,
        userId: user.id,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {
      setLoading(false);

      if (err.response?.status === 403) {
        const errorData = err.response.data;
        setOpenAlert(true);
        setUserTicketOpen(errorData.ticket?.user?.name || "Outro usuário");
        setQueueTicketOpen(errorData.ticket?.queue?.name || "Sem fila");
        return;
      }

      if (err.response?.status === 409) {
        try {
          const ticket = JSON.parse(err.response.data.error);
          if (ticket.userId !== user?.id) {
            setOpenAlert(true);
            setUserTicketOpen(ticket?.user?.name);
            setQueueTicketOpen(ticket?.queue?.name);
          } else {
            setOpenAlert(false);
            setUserTicketOpen("");
            setQueueTicketOpen("");
            setLoading(false);
            onClose(ticket);
          }
        } catch (_) {
          toastError(err);
        }
      } else {
        toastError(err);
      }
    }
    setLoading(false);
  };

  const handleSaveTicket = async (contactId) => {
    return handleSaveTicketWithQueue(contactId, selectedQueue);
  };

  const handleQuickNumberSelect = async (rawNumber) => {
    const cleanNumber = normalizeNumberForStorage(rawNumber);
    const formatted = formatPhoneDisplay(rawNumber);

    setLoading(true);
    try {
      let contact = null;
      try {
        const { data } = await api.post("/contacts", {
          name: formatted,
          number: cleanNumber,
        });
        contact = data;
      } catch (err) {
        // Se já existe contato com esse número, recupera
        if (err?.response?.data?.error === "ERR_DUPLICATED_CONTACT" || err?.response?.status === 400) {
          const { data } = await api.get("contacts", {
            params: { searchParam: cleanNumber },
          });
          contact = data?.contacts?.find((c) => c.number === cleanNumber) || data?.contacts?.[0];
        } else {
          throw err;
        }
      }

      if (contact) {
        setSelectedContact(contact);
        setOptions([contact]);

        let targetQueue = selectedQueue;
        if (!targetQueue && user?.queues?.length > 0) {
          targetQueue = user.queues[0].id;
          setSelectedQueue(targetQueue);
        }

        if (targetQueue) {
          await handleSaveTicketWithQueue(contact.id, targetQueue);
        } else {
          toast.info(`Número ${formatted} selecionado. Selecione uma fila para iniciar.`);
        }
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (e, newValue) => {
    if (!newValue) return;

    if (newValue.isQuickNumber) {
      handleQuickNumberSelect(newValue.rawNumber);
    } else if (newValue.number) {
      setSelectedContact(newValue);
    } else if (newValue.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = (contact) => {
    setSelectedContact(contact);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    const inputVal = params.inputValue.trim();

    if (inputVal !== "" && !loading) {
      const digitsOnly = inputVal.replace(/\D/g, "");

      // Se digitou pelo menos 8 números, adiciona opção de conversa rápida
      if (digitsOnly.length >= 8) {
        const formatted = formatPhoneDisplay(inputVal);
        filtered.unshift({
          isQuickNumber: true,
          rawNumber: inputVal,
          name: formatted,
          display: `💬 Conversar com ${formatted}`,
        });
      } else if (searchParam.length >= 3) {
        filtered.push({
          name: `${inputVal}`,
        });
      }
    }
    return filtered;
  };

  const renderOption = (option) => {
    if (option.isQuickNumber) {
      return (
        <div className={classes.quickOption}>
          <WhatsAppIcon style={{ color: "#25D366" }} />
          <Typography component="span" style={{ fontSize: 14, fontWeight: 700 }}>
            💬 Conversar com {option.name} <span style={{ fontSize: 11, fontWeight: 400, color: "#666" }}>(Iniciar chat direto)</span>
          </Typography>
        </div>
      );
    }

    if (option.number) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
          <Avatar
            src={resolveContactAvatar(option)}
            imgProps={{ onError: handleImageError }}
            style={{ width: 40, height: 40, flexShrink: 0 }}
          />
          <Typography
            component="span"
            style={{
              fontSize: 14,
              lineHeight: 1.35,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {IconChannel(option.channel)}
            {option.name} - {option.number}
          </Typography>
        </div>
      );
    }
    return `${i18n.t("newTicketModal.add")} ${option.name}`;
  };

  const renderOptionLabel = (option) => {
    if (option.isQuickNumber) {
      return `Conversar com ${option.name}`;
    }
    if (option.number) {
      return `${option.name} - ${option.number}`;
    }
    return `${option.name}`;
  };

  const renderContactAutocomplete = () => {
    if (initialContact === undefined || initialContact.id === undefined) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            value={selectedContact}
            onChange={(e, newValue) => {
              setChannelFilter(newValue?.channel ? newValue.channel : "whatsapp");
              handleSelectOption(e, newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nome ou Número do WhatsApp"
                placeholder="Ex: 86999998888 ou João"
                variant="outlined"
                autoFocus
                onChange={(e) => setSearchParam(e.target.value)}
                onKeyPress={(e) => {
                  if (loading || !selectedContact) return;
                  if (e.key === "Enter") {
                    handleSaveTicket(selectedContact.id);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      );
    }
    return null;
  };

  return (
    <>
      <Dialog open={modalOpen} onClose={handleClose}>
        <DialogTitle id="form-dialog-title">
          {i18n.t("newTicketModal.title")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid style={{ width: 320 }} container spacing={2}>
            {/* CONTATO OU NÚMERO */}
            {renderContactAutocomplete()}

            {/* FILA */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedQueue}
                onChange={(e) => {
                  setSelectedQueue(e.target.value);
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedQueue === "") {
                    return "Selecione uma fila";
                  }
                  const queue = user.queues?.find((q) => q.id === selectedQueue);
                  return queue?.name || "Selecione uma fila";
                }}
              >
                {user.queues?.length > 0 &&
                  [...user.queues]
                    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }))
                    .map((queue, key) => (
                      <MenuItem dense key={key} value={queue.id}>
                        <ListItemText primary={queue.name} />
                      </MenuItem>
                    ))}
              </Select>
            </Grid>

            {/* CONEXÃO */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedWhatsapp}
                onChange={(e) => {
                  setSelectedWhatsapp(e.target.value);
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === "") {
                    return "Selecione uma Conexão";
                  }
                  const whatsapp = whatsapps.find((w) => w.id === selectedWhatsapp);
                  return whatsapp?.name || "Selecione uma Conexão";
                }}
              >
                {whatsapps?.length > 0 &&
                  userAllowedWhatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={whatsapp.id}>
                      <ListItemText
                        primary={
                          <>
                            {IconChannel(whatsapp.channel)}
                            <Typography
                              component="span"
                              style={{
                                fontSize: 14,
                                marginLeft: "10px",
                                display: "inline-flex",
                                alignItems: "center",
                                lineHeight: "2",
                              }}
                            >
                              {whatsapp.name} &nbsp;{" "}
                              <p
                                className={
                                  whatsapp.status === "CONNECTED"
                                    ? classes.online
                                    : classes.offline
                                }
                              >
                                ({whatsapp.status})
                              </p>
                            </Typography>
                          </>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={loading}
            variant="outlined"
          >
            {i18n.t("newTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="button"
            disabled={!selectedContact || selectedQueue === ""}
            onClick={() => handleSaveTicket(selectedContact.id)}
            color="primary"
            loading={loading}
          >
            {i18n.t("newTicketModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            initialValues={newContact}
            onClose={handleCloseContactModal}
            onSave={handleAddNewContactTicket}
          />
        )}
        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={handleCloseAlert}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog>
    </>
  );
};
export default NewTicketModal;
